import type { ChartTheme } from '@/components/charts/theme';
import type { WarningTier } from '@/domain/feedingDashboard';

/**
 * Shared palette for the feeding dashboard charts (IPS sketch). Plain values because a canvas
 * cannot resolve `var()`. Light values: the charts draw them through the chart theme, which
 * lifts the target line to the dark theme's muted text.
 */
export const FEEDING_COLORS = {
    pms: '#5b7fa6',
    pmsMuted: 'rgba(91, 127, 166, 0.55)',
    bioscope: '#2fa67a',
    bioscopeMuted: 'rgba(47, 166, 122, 0.55)',
    plan: '#8aa9c9',
    temperature: '#e0813a',
    oxygen: '#4a90d9',
    // Saturation is a different quantity from concentration, so it gets its own hue
    // rather than a shade of the oxygen blue, which would imply the same series. Pink
    // ($accent-pink-400): the earlier teal sat next to the Weight Bioscope green and read
    // as the weight line collapsing.
    oxygenSaturation: '#ce7997',
    salinity: '#8e6bbf',
    // $neutral-700, the muted text of the table header and captions under the charts. In the
    // dark theme it is drawn as the chart text colour (`PALETTE_LIFT_TOKENS`).
    target: '#5d6182',
} as const;

/** Thick enough to read at a glance, thin enough not to eat into a narrow bar. */
export const WARNED_BAR_OUTLINE_WIDTH = 2;

/** The portal's warning-state colours for a filled chart mark such as a bar. */
export interface WarningPalette {
    warning: string;
    critical: string;
    /** Outline of a warned bar: the chart's label ink, dark on the light card and light on the dark one. */
    outline: string;
}

/**
 * The warning colours as the chart theme has them, because a canvas cannot resolve `var()`.
 *
 * A bar is a filled mark read at a glance against the card, so it takes the strongest shade
 * of each scale: the pale -50 background the table cells use would all but vanish as a bar.
 * The Operational dashboard's warning glyph uses the same -600 shade. Both fills keep their
 * value in the dark theme; the outline switches to light ink so a warned bar stays outlined.
 */
export function warningPalette(theme: ChartTheme): WarningPalette {
    return {
        warning: theme.resolve('alert-warning-600'),
        critical: theme.resolve('alert-critical-400'),
        outline: theme.labelInk,
    };
}

/**
 * A bar's colour for its warning tier. Outside the warning state the bar keeps its series
 * colour, so the legend swatch still identifies it; only the shared rule's tiers recolour
 * it, which keeps a bar and its pen table cell in the same state.
 */
export function toneColor(tier: WarningTier, base: string, palette: WarningPalette): string {
    if (tier === 'warning') return palette.warning;
    if (tier === 'critical') return palette.critical;
    return base;
}

/**
 * A warned bar's outline, or none on target. The fill alone does not carry the state: the
 * warning fill is 1.7:1 against white, below the 3:1 a graphical object needs, and the
 * critical fill has almost the Bioscope series' lightness, so a reader who cannot tell the
 * hues apart would see no difference. The dark outline is far above 3:1 on both.
 */
export function toneOutline(tier: WarningTier, palette: WarningPalette): string | undefined {
    return tier === 'none' ? undefined : palette.outline;
}

/**
 * A value's band against its configured thresholds: `good` in band, `warning` in the
 * caution band, `alert` beyond it. `default` means no verdict (a missing value, or one
 * with no meaningful good/bad direction). The KPI cards and the pen table draw the bands
 * in the information palette, since only the four bFCR and RGI conditions of the shared
 * warning rule may use the warning state. Pellet fallthrough borrows the warning colours
 * for its bands instead, without the warning state (`pelletTier`).
 *
 * Every threshold is supplied by the caller from the per-location config; the
 * percentage bands are in their natural unit (5 = 5 %).
 */
export type KpiTone = 'good' | 'warning' | 'alert' | 'default';

/**
 * Feed vs plan, banded by SIGN rather than magnitude: the Pen Comparison spec
 * states "fed more than plan = positive, shown in green; fed less than plan =
 * negative, shown in red". Underfeeding is the operational risk, so a symmetric
 * band would flag a healthy overfeed and an actual shortfall the same way.
 * Exactly on plan counts as good.
 */
export function feedVsPlanTone(pct: number | null): KpiTone {
    if (pct == null) return 'default';
    return pct >= 0 ? 'good' : 'alert';
}

/**
 * SFR (utfôringsprosent) against the healthy range.
 *
 * The Pen Comparison spec gives the biology: "Healthy range for salmon: 1-2%. Above
 * ~3% may mean overfeeding." Below the healthy range is flagged as caution too, since
 * underfeeding is a real condition even though the spec sets no hard floor for it.
 *
 * SFR marks its own cell but must never put a pen in the warning state: the spec calls it
 * "for information only". Keeping it out of `penWarnings` is what enforces that.
 */
export function sfrTone(
    pct: number | null,
    healthyMinPct: number,
    healthyMaxPct: number,
    overfeedPct: number
): KpiTone {
    if (pct == null) return 'default';
    if (pct > overfeedPct) return 'alert';
    if (pct >= healthyMinPct && pct <= healthyMaxPct) return 'good';
    return 'warning';
}

/**
 * Fallthrough % against its thresholds: good under the warn threshold, caution up to the
 * alert threshold, alert above. Every pellet surface draws the two bands, elevated and
 * high, in the warning colours (`pelletTier`, `fallthroughColor`).
 */
export function fallthroughTone(pct: number | null, warnPct: number, alertPct: number): KpiTone {
    if (pct == null) return 'default';
    if (pct > alertPct) return 'alert';
    if (pct >= warnPct) return 'warning';
    return 'good';
}

/**
 * FCR confidence interval as a ratio of the estimate: good ≤goodPct %, caution
 * ≤warnPct %, alert wider. The bands are percentages; the ratio is unitless.
 */
export function confIntervalTone(ratio: number | null, goodPct: number, warnPct: number): KpiTone {
    if (ratio == null) return 'default';
    if (ratio <= goodPct / 100) return 'good';
    if (ratio <= warnPct / 100) return 'warning';
    return 'alert';
}
